import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Gift, Star, Award } from "lucide-react";

/**
 * PointsToast — global celebration toast for the loyalty/referral system.
 *
 * Reinforces the new teal = "earn / reward" visual language: every time a
 * client earns Crown Points, a small teal card slides in with a +N pts
 * burst, sparkle, and a quick reason. Auto-dismisses after 3.6s.
 *
 * Trigger from ANY component (no prop drilling) via:
 *
 *   window.dispatchEvent(new CustomEvent("award-points-toast", {
 *     detail: { points: 1000, reason: "Referral booked" }
 *   }));
 *
 * Mounted once in App.js root so it's available site-wide.
 */
const PointsToast = () => {
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    const handler = (ev) => {
      const { points, reason, icon } = ev.detail || {};
      if (!points || typeof points !== "number") return;
      setQueue((q) => [...q, { id: Date.now() + Math.random(), points, reason: reason || "Points earned", icon: icon || "sparkles" }]);
    };
    window.addEventListener("award-points-toast", handler);
    return () => window.removeEventListener("award-points-toast", handler);
  }, []);

  // Drain queue one toast at a time
  useEffect(() => {
    if (current || queue.length === 0) return;
    const next = queue[0];
    setQueue((q) => q.slice(1));
    setCurrent(next);
    // Try haptic feedback on mobile (no-op on desktop)
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      try { navigator.vibrate([12, 40, 12]); } catch (_) { /* noop */ }
    }
    const t = setTimeout(() => setCurrent(null), 3600);
    return () => clearTimeout(t);
  }, [queue, current]);

  const iconMap = { sparkles: Sparkles, gift: Gift, star: Star, award: Award };
  const Icon = current ? (iconMap[current.icon] || Sparkles) : Sparkles;

  return (
    <AnimatePresence>
      {current && (
        <motion.div
          key={current.id}
          initial={{ opacity: 0, y: -24, scale: 0.92 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -16, scale: 0.96 }}
          transition={{ type: "spring", stiffness: 320, damping: 24 }}
          className="fixed top-24 sm:top-28 left-1/2 -translate-x-1/2 z-[300] rounded-2xl shadow-2xl overflow-hidden pointer-events-auto"
          style={{
            background: "linear-gradient(135deg, #0d9488 0%, #14b8a6 35%, #2dd4bf 70%, #14b8a6 100%)",
            boxShadow: "0 20px 50px -10px rgba(20,184,166,0.45)",
            border: "1px solid rgba(45,212,191,0.5)",
            maxWidth: "calc(100vw - 32px)",
            width: "max-content",
          }}
          data-testid="points-toast"
        >
          {/* Sparkle burst — tiny animated stars layered behind the text */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(6)].map((_, i) => (
              <motion.span
                key={i}
                className="absolute w-1 h-1 rounded-full bg-white/80"
                initial={{ opacity: 0, scale: 0, x: 60, y: 28 }}
                animate={{
                  opacity: [0, 1, 0],
                  scale: [0, 1.4, 0],
                  x: 60 + (Math.cos((i / 6) * Math.PI * 2) * 40),
                  y: 28 + (Math.sin((i / 6) * Math.PI * 2) * 24),
                }}
                transition={{ duration: 1.1, delay: 0.15 + i * 0.04, ease: "easeOut" }}
              />
            ))}
          </div>

          <div className="relative flex items-center gap-3 px-4 py-3 sm:px-5 sm:py-3.5 text-white">
            {/* Icon disk */}
            <div className="flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/20 backdrop-blur flex items-center justify-center ring-1 ring-white/30">
              <Icon className="w-5 h-5" />
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-1.5">
                <motion.span
                  initial={{ y: 6, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.08 }}
                  className="text-xl sm:text-2xl font-bold tracking-tight"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  +{current.points.toLocaleString()}
                </motion.span>
                <span className="text-[11px] sm:text-xs uppercase tracking-widest font-semibold opacity-90">
                  pts
                </span>
              </div>
              <p className="text-[11px] sm:text-xs opacity-95 leading-tight mt-0.5 truncate">
                {current.reason}
              </p>
            </div>
          </div>

          {/* Bottom progress bar that drains for the 3.6s display window */}
          <motion.div
            initial={{ scaleX: 1 }}
            animate={{ scaleX: 0 }}
            transition={{ duration: 3.4, ease: "linear" }}
            className="h-0.5 bg-white/40 origin-left"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * Convenience helper — fires a points toast from anywhere in the app.
 *
 * Usage:
 *   import { firePointsToast } from "@/components/PointsToast";
 *   firePointsToast(1000, "Friend just booked!", "gift");
 */
export const firePointsToast = (points, reason, icon = "sparkles") => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("award-points-toast", { detail: { points, reason, icon } }));
};

export default PointsToast;
